// contexts/ReportsContext.js
import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "../Utility/AuthProvider";
import { useSettings } from "./SettingsContext";
import { AppSettings } from "./SettingsContext";
import APICall from "../Utility/APICall";
import { paths } from "../Utility/Constants";

// Create contexts
const ReportsDataContext = createContext();
const ReportsDataUpdateContext = createContext();

// Custom hooks to use the contexts
export function useReportsData() {
  return useContext(ReportsDataContext);
}

export function useUpdateReportsData() {
  return useContext(ReportsDataUpdateContext);
}

// 🔥 UTILITY FUNCTION for better amount formatting
export const formatAmount = (amount) => {
  if (!amount || amount === 0) return "₹0";

  // Handle very large amounts (probably errors)
  if (amount > 10000000) {
    // 1 crore
    return "₹" + (amount / 10000000).toFixed(1) + "Cr";
  }

  if (amount > 100000) {
    // 1 lakh
    return "₹" + (amount / 100000).toFixed(1) + "L";
  }

  if (amount > 1000) {
    // 1 thousand
    return "₹" + (amount / 1000).toFixed(1) + "K";
  }

  return "₹" + amount.toLocaleString("en-IN");
};

// 🔥 DEBUGGING function to check amount values
export const debugAmountValues = (data) => {
  console.log("🔍 Debugging Amount Values:");

  data.forEach((section, sectionIndex) => {
    console.log(`📊 Section ${sectionIndex + 1}: ${section.title}`);

    section.data.forEach((item, itemIndex) => {
      const amounts = {
        amount: item.amount,
        local_amount: item.local_amount,
        type: typeof item.amount,
        isValidNumber: !isNaN(item.amount) && item.amount > 0,
      };

      if (item.amount > 1000000) {
        // Flag suspicious amounts
        console.warn(`⚠️ Large amount detected:`, amounts);
      }

      if (itemIndex < 3) {
        // Show first 3 items per section
        console.log(`  Item ${itemIndex + 1}:`, amounts);
      }
    });
  });
};

// 🔥 DEBUGGING: Add this function to help troubleshoot
export const debugSelectedTenant = (selectedTenant, allData) => {
  console.log("🐛 DEBUG: Selected Tenant Details");
  console.log("Selected tenant:", selectedTenant);

  console.log("🔍 Looking for matches in data:");
  allData.forEach((section, sectionIndex) => {
    section.data.forEach((item, itemIndex) => {
      const isMatch =
        item.tenant_id === selectedTenant.tenant_id &&
        item.agreement_id === selectedTenant.agreement_id &&
        item.unit_id === selectedTenant.unit_id &&
        item.month === selectedTenant.month &&
        item.year === selectedTenant.year &&
        item.nature === selectedTenant.nature;

      if (isMatch) {
        console.log(
          `✅ MATCH found in section ${sectionIndex}, item ${itemIndex}:`,
          item
        );
      }
    });
  });
};

// Loading states (similar to mobile version)
export const Loading = {
  RESET: "RESET",
  LOADING: "LOADING",
  AUTO_REFRESHING: "AUTO_REFRESHING",
  MANUAL_REFRESHING: "MANUAL_REFRESHING",
  PROGRAMMATIC_REFRESHING: "PROGRAMMATIC_REFRESHING",

  allLoadingStates: [
    "LOADING",
    "AUTO_REFRESHING",
    "MANUAL_REFRESHING",
    "PROGRAMMATIC_REFRESHING",
  ],

  shouldLoad: (lastUpdated, cacheTimeout = 5 * 60 * 1000) => {
    // 5 minutes cache
    if (!lastUpdated) return true;
    return Date.now() - lastUpdated > cacheTimeout;
  },
};

// Local storage utilities
const storage = {
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Error storing data:", error);
    }
  },

  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error("Error retrieving data:", error);
      return null;
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing data:", error);
    }
  },
};

// Initial data structure (matching mobile version)
const initialData = {
  rentPieChartData: [],
  showNoAgreement: true,
  showNoTransactionRemaining: true,
  isPieChart: false,
  expiringAgreements: [],
  averageMonthlyRent: 0,
  rentData: {
    all: [],
  },
  lastUpdated: null,
};

export function ReportsDataProvider({ children }) {
  const { jwt } = useAuth();
  const settings = useSettings();
  const reportsDataKey = "reportsData";

  const [data, setData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to get settings value with fallback
  const getSettingsValue = (property) => {
    return settings?.[property] === undefined
      ? AppSettings.getDefaultValue(property)
      : settings[property];
  };

  // Initialize data from localStorage on mount
  useEffect(() => {
    getDataFromStorage();
  }, []);

  // 🔥 CRITICAL: Watch for settings changes and refetch data
  useEffect(() => {
    if (settings && jwt) {
      console.log("Settings changed, refetching data...");
      console.log("Current settings:", {
        showFutureReports: getSettingsValue(AppSettings.SHOW_FUTURE_REPORTS),
        sortReportsByLatestFirst: getSettingsValue(
          AppSettings.SORT_REPORTS_BY_LATEST_FIRST
        ),
      });

      // Force refetch when these critical settings change
      updateData(Loading.PROGRAMMATIC_REFRESHING);
    }
  }, [
    settings?.[AppSettings.SHOW_FUTURE_REPORTS],
    settings?.[AppSettings.SORT_REPORTS_BY_LATEST_FIRST],
    jwt,
  ]);

  // Function to retrieve data from localStorage
  const getDataFromStorage = () => {
    try {
      const storedData = storage.get(reportsDataKey);
      if (storedData) {
        setData(storedData);
      }
    } catch (error) {
      console.error("Error retrieving reports data from storage:", error);
    }
  };

  // Process sectioned data based on mobile app structure
  const processSectionedData = (rentData) => {
    if (!rentData || rentData.length === 0) return [];

    // If data already has sections with title and data properties
    if (rentData[0]?.title && rentData[0]?.data) {
      return sortSections([...rentData]);
    }

    // If it's flat data, organize into sections
    const sections = [];
    const securityDeposits = rentData.filter(
      (item) => item.nature === "security_deposit"
    );
    const monthlyRents = rentData.filter(
      (item) => item.nature !== "security_deposit"
    );

    // Add security deposits section
    if (securityDeposits.length > 0) {
      sections.push({
        title: "Security Deposit",
        data: securityDeposits,
      });
    }

    // Group monthly rents by month/year
    const monthGroups = {};
    monthlyRents.forEach((item) => {
      const key = `${item.month} ${item.year}`;
      if (!monthGroups[key]) {
        monthGroups[key] = { title: key, data: [] };
      }
      monthGroups[key].data.push(item);
    });

    sections.push(...Object.values(monthGroups));
    return sortSections(sections);
  };

  // Sort sections based on settings
  const sortSections = (sections) => {
    const sortByLatest = getSettingsValue(
      AppSettings.SORT_REPORTS_BY_LATEST_FIRST
    );

    const securityDeposit = sections.find(
      (section) => section.title === "Security Deposit"
    );
    const monthSections = sections
      .filter((section) => section.title !== "Security Deposit")
      .sort((a, b) => {
        const dateA = new Date(a.title);
        const dateB = new Date(b.title);
        return sortByLatest ? dateB - dateA : dateA - dateB;
      });

    return securityDeposit
      ? [securityDeposit, ...monthSections]
      : monthSections;
  };

  // Main function to fetch reports data from API
  const getReportsData = async () => {
    try {
      setIsLoading(true);

      const params = {};

      // 🔥 CRITICAL: Add settings-based parameters (exactly like RentIncome)
      const showFutureReports = getSettingsValue(
        AppSettings.SHOW_FUTURE_REPORTS
      );
      params.show_future_rent = showFutureReports ? "true" : "false";

      const sortByLatest = getSettingsValue(
        AppSettings.SORT_REPORTS_BY_LATEST_FIRST
      );
      params.reverse_order = sortByLatest ? "true" : "false";

      const language = getSettingsValue(AppSettings.LANGUAGE);
      if (language && language !== "english") {
        params.language = language;
      }

      console.log("🚀 Fetching rent overview with params:", params);
      console.log("📋 Settings values:", {
        showFutureReports,
        sortByLatest,
        language,
      });

      const response = await APICall(
        "GET",
        paths.rent.overview,
        undefined,
        params,
        jwt
      );

      console.log("📥 Reports API response:", response);

      if (response?.status === "success" && response?.response) {
        const apiResponse = response.response;

        console.log("🔍 API Response structure:", {
          rent_data_length: apiResponse.rent_data?.length || 0,
          rent_overview_length: apiResponse.rent_overview?.length || 0,
          show_no_agreement: apiResponse.show_no_agreement,
          show_no_transaction_remaining:
            apiResponse.show_no_transaction_remaining,
        });

        // Process the data to match expected structure
        let processedRentData = [];
        if (apiResponse.rent_data && Array.isArray(apiResponse.rent_data)) {
          console.log(
            "📊 Processing rent_data array of length:",
            apiResponse.rent_data.length
          );
          processedRentData = processSectionedData(apiResponse.rent_data);
        } else {
          console.warn("⚠️ No rent_data found in API response");
        }

        return {
          rentPieChartData: apiResponse.rent_overview || [],
          showNoAgreement: apiResponse.show_no_agreement ?? true,
          showNoTransactionRemaining:
            apiResponse.show_no_transaction_remaining ?? true,
          isPieChart: apiResponse.is_pie_chart ?? false,
          expiringAgreements: apiResponse.expiring_agreements || [],
          averageMonthlyRent: apiResponse.average_monthly_rent || 0,
          rentData: {
            all: processedRentData,
          },
        };
      }

      return data; // Return current data if API fails
    } catch (error) {
      console.error("Error fetching Reports data:", error);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  // Update data function (similar to mobile version)
  const updateData = async (newData) => {
    let dataToStore = {};

    const getReportsFromAPI = async () => {
      dataToStore = await getReportsData();
    };

    if (newData === Loading.RESET) {
      console.log("Resetting Reports");
      setData(initialData);
      storage.set(reportsDataKey, initialData);
      return;
    } else if (Loading.allLoadingStates.includes(newData)) {
      switch (newData) {
        case Loading.AUTO_REFRESHING:
        case Loading.LOADING:
          if (Loading.shouldLoad(data.lastUpdated)) {
            await getReportsFromAPI();
          } else {
            return; // Don't update if cache is still valid
          }
          break;
        default:
          await getReportsFromAPI();
          break;
      }
      dataToStore.lastUpdated = Date.now();
    } else if (typeof newData === "object") {
      dataToStore = newData;
      dataToStore.lastUpdated = data.lastUpdated;
    } else {
      return;
    }

    setData(dataToStore);
    storage.set(reportsDataKey, dataToStore);
  };

  // Helper functions for local data updates (for mark as paid functionality)
  const updateLocalData = (updatedData) => {
    const newData = { ...data, ...updatedData, lastUpdated: data.lastUpdated };
    setData(newData);
    storage.set(reportsDataKey, newData);
  };

  // Function to handle mark as paid locally (similar to mobile version)
  const handleMarkAsPaidLocally = (selectedTenant) => {
    if (!selectedTenant) {
      console.error("❌ No selectedTenant provided to handleMarkAsPaidLocally");
      return;
    }

    console.log("🔄 Starting local mark as paid update for:", selectedTenant);

    // 🔥 ADD VALIDATION for required fields
    if (!selectedTenant.tenant_id || !selectedTenant.agreement_id) {
      console.error("❌ Missing required tenant identifiers:", selectedTenant);
      return;
    }

    const tenantMonthYear = `${selectedTenant.month} ${selectedTenant.year}`;
    const newRentPieChartData = [...(data.rentPieChartData || [])];

    // Update pie chart data for rent payments
    if (selectedTenant.nature === "rent") {
      console.log("💰 Updating pie chart data for rent payment");
      newRentPieChartData.forEach((chartData) => {
        if (chartData.title === tenantMonthYear) {
          const oldReceivedRent = chartData.received_rent;
          chartData.received_rent =
            parseInt(chartData.received_rent) + parseInt(selectedTenant.amount);
          chartData.tenants_paid = parseInt(chartData.tenants_paid) + 1;

          console.log(
            `📈 Updated ${tenantMonthYear}: ${oldReceivedRent} → ${chartData.received_rent}`
          );
        }
      });

      // Recalculate trends and percentages
      if (newRentPieChartData.length > 0) {
        const maxReceivedRent = Math.max(
          ...newRentPieChartData.map((data) => data.received_rent)
        );

        newRentPieChartData.forEach((chartData, index) => {
          const dataToCompare =
            newRentPieChartData.length > 1 && index === 0
              ? newRentPieChartData[1]
              : newRentPieChartData[0] || null;

          if (dataToCompare) {
            chartData.is_trend_up =
              chartData.received_rent > dataToCompare.received_rent;
            const rentComparison = dataToCompare.received_rent;
            const rentDifferencePercentage =
              ((chartData.received_rent - rentComparison) /
                Math.max(rentComparison, 1)) *
              100;
            chartData.percentage = Math.abs(rentDifferencePercentage).toFixed(
              2
            );
            chartData.relative_height =
              chartData.received_rent / Math.max(maxReceivedRent, 1);
          }
        });
      }
    }

    // 🔥 ENHANCED item removal with better matching
    console.log("🗑️ Removing paid item from rent data...");

    let itemsRemovedCount = 0;
    let newAllRentData = data.rentData.all
      .map((section) => {
        const filteredData = section.data.filter((item) => {
          const isMatchingItem =
            item.tenant_id === selectedTenant.tenant_id &&
            item.agreement_id === selectedTenant.agreement_id &&
            item.unit_id === selectedTenant.unit_id &&
            item.month === selectedTenant.month &&
            item.year === selectedTenant.year &&
            item.nature === selectedTenant.nature &&
            Math.abs(item.amount - selectedTenant.amount) < 1; // Allow small rounding differences

          if (isMatchingItem) {
            console.log("🎯 Found matching item to remove:", {
              tenant_id: item.tenant_id,
              month: item.month,
              year: item.year,
              nature: item.nature,
              amount: item.amount,
            });
            itemsRemovedCount++;
          }

          return !isMatchingItem;
        });

        return {
          ...section,
          data: filteredData,
        };
      })
      .filter((section) => section.data.length > 0);

    console.log(`✅ Removed ${itemsRemovedCount} item(s) from rent data`);

    // Handle next month's warning state for rent payments
    if (
      selectedTenant.nature === "rent" &&
      !selectedTenant.is_last_month_in_agreement
    ) {
      const nextTenantMonthYear = getNextMonth(
        selectedTenant.month,
        selectedTenant.year
      );
      const [nextTenantMonth, nextTenantYear] = nextTenantMonthYear.split(" ");

      const nextItem = newAllRentData.find(
        (item) =>
          item.title === nextTenantMonthYear &&
          item.data.some(
            (innerItem) =>
              innerItem.tenant_id === selectedTenant.tenant_id &&
              innerItem.agreement_id === selectedTenant.agreement_id
          )
      );

      if (nextItem) {
        console.log("🚨 Updating next month warning status");
        nextItem.data.forEach((innerItem) => {
          if (
            innerItem.month === nextTenantMonth &&
            innerItem.year === nextTenantYear &&
            innerItem.tenant_id === selectedTenant.tenant_id &&
            innerItem.agreement_id === selectedTenant.agreement_id
          ) {
            innerItem.mark_as_paid_warning = false;
            console.log("✅ Removed mark_as_paid_warning for next month");
          }
        });
      }
    }

    // Update derived values
    const newShowNoTransactionRemaining = newAllRentData.length === 0;
    const totalReceivedRent = newRentPieChartData.reduce(
      (sum, data) => sum + (data.received_rent || 0),
      0
    );
    const newAverageMonthlyRent = Math.floor(
      totalReceivedRent / Math.max(newRentPieChartData.length, 1)
    );

    const updatedData = {
      ...data,
      rentPieChartData: newRentPieChartData,
      showNoTransactionRemaining: newShowNoTransactionRemaining,
      averageMonthlyRent: newAverageMonthlyRent,
      rentData: {
        ...data.rentData,
        all: newAllRentData,
      },
    };

    console.log("💾 Updating context data...");
    updateLocalData(updatedData);
    console.log("✅ Local mark as paid update completed");
  };

  // 🔥 ENHANCED getNextMonth function for local updates
  const getNextMonth = (currentMonth, currentYear) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const currentMonthIndex = months.findIndex(
      (month) => month.toLowerCase() === currentMonth.toLowerCase()
    );

    if (currentMonthIndex === -1) {
      console.warn(`Invalid month: ${currentMonth}`);
      return `${currentMonth} ${currentYear}`; // Return as-is if invalid
    }

    const nextMonthIndex = (currentMonthIndex + 1) % 12;
    const nextYear =
      nextMonthIndex === 0 ? parseInt(currentYear) + 1 : parseInt(currentYear);

    return `${months[nextMonthIndex]} ${nextYear}`;
  };

  // Extended context value with additional helper functions
  const contextValue = {
    ...data,
    isLoading,
    // Helper functions
    handleMarkAsPaidLocally,
    updateLocalData,
    getSettingsValue,
  };

  return (
    <ReportsDataContext.Provider value={contextValue}>
      <ReportsDataUpdateContext.Provider value={updateData}>
        {children}
      </ReportsDataUpdateContext.Provider>
    </ReportsDataContext.Provider>
  );
}

// Export default for easier importing
export default ReportsDataProvider;
